'use client';

// "Withdraw from contract here": the online withdrawal function EU/EEA/UK
// consumers are owed for as long as their 14-day right lasts (CRD Art. 11a,
// inserted by Directive (EU) 2023/2673, applying from 19 June 2026). The buyer
// names themselves and the contract, confirms, and gets an acknowledgement by
// email at once; the line is refunded straight away.
//
// The server decides whether the right is still open (functions:
// withdrawFromContract, lib/withdrawal-right): 14 days from the purchase, ending
// early only once a download starts on a consent the receipt confirmed. This
// component pre-filters on what the page already knows (country, date, refund)
// so it only asks the server about orders the right can apply to.

import { useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { requiresWithdrawalConsent, WITHDRAWAL_PERIOD_DAYS } from '@/lib/withdrawal-right';
import { userErrorMessage } from '@/lib/user-error-message';

const DAY_MS = 24 * 60 * 60 * 1000;

type CheckResult = {
  open: boolean; reason: string | null; closesAtMs: number;
  /** Starting a download ends the right (only on a consent the receipt confirmed). */
  endsOnDownload?: boolean;
  /** Opening an access link ends it too (only on the consent wording that says so). */
  endsOnAccessLink?: boolean;
};
type ConfirmResult = { received: boolean; refundIssued: boolean; refundId: string; submittedAtMs: number };

/** The order reference a receipt prints, so the statement names the same thing. */
function orderRef(orderId: string): string {
  return `#${orderId.replace(/^int_/, '').slice(0, 12).toUpperCase()}`;
}

export type WithdrawTarget = {
  orderId: string;
  productId: string;
  productName: string;
  /**
   * A country the right applies in, used only to decide whether to ask the
   * server: the order's withdrawal country where the page has it, else the
   * viewer's. Outside the right's countries nothing is asked or shown.
   */
  country: string | null | undefined;
  /** When the order was paid, epoch millis; 0 when the page does not know. */
  paidAtMs: number;
  refunded: boolean;
  /** A guest proves the order with its email; omit for a signed-in buyer. */
  guestEmail?: string;
};

/**
 * The pre-filter's facts from an account's purchase record: the ORDER's country
 * (where its right was judged) and when it was paid, so a buyer abroad or on a
 * VPN still sees the control and nobody is asked about an order long past its
 * 14 days. Without a record (a guest order the page does not read) the viewer's
 * country stands in, and the server decides either way.
 *
 * @param record users/{uid}/purchases/{intentId}, if the page has it.
 * @param productId The line.
 * @param fallbackCountry The viewer's country.
 */
export function withdrawFactsFromPurchase(
  record: Record<string, unknown> | undefined,
  productId: string,
  fallbackCountry: string | null | undefined,
): Pick<WithdrawTarget, 'country' | 'paidAtMs' | 'refunded'> {
  const consentCountry = (record?.withdrawalConsent as { country?: unknown } | undefined)?.country;
  const country = typeof consentCountry === 'string' && consentCountry ? consentCountry :
    typeof record?.taxCountry === 'string' && record.taxCountry ? record.taxCountry : fallbackCountry;
  const created = record?.createdAt as { toMillis?: () => number } | undefined;
  const refunded = Array.isArray(record?.refundedProductIds) &&
    (record.refundedProductIds as unknown[]).includes(productId);
  return { country, paidAtMs: typeof created?.toMillis === 'function' ? created.toMillis() : 0, refunded };
}

/**
 * Whether the withdrawal function is open for one order line. Null while
 * unknown (and for orders the right cannot apply to, without asking the server).
 */
export function useWithdrawalOpen(target: WithdrawTarget | null): CheckResult | null {
  const [result, setResult] = useState<CheckResult | null>(null);
  const key = target ? `${target.orderId}:${target.productId}:${target.refunded}` : '';
  useEffect(() => {
    setResult(null);
    if (!target) return;
    // Pre-filter on what the page knows; the server re-checks everything, and
    // judges the ORDER's country and payment time, whatever the page passed.
    if (target.refunded || !requiresWithdrawalConsent(target.country)) return;
    // 0 means the page does not know when the order was paid: ask the server.
    // The period ends with the 14th day after the purchase day wherever the buyer
    // is, which can run a day and a half past 14 x 24 hours, so allow two days.
    if (target.paidAtMs && Date.now() > target.paidAtMs + (WITHDRAWAL_PERIOD_DAYS + 2) * DAY_MS) return;
    let cancelled = false;
    const call = httpsCallable<Record<string, unknown>, CheckResult>(getFunctions(), 'withdrawFromContract');
    call({
      mode: 'check', orderId: target.orderId, productId: target.productId,
      ...(target.guestEmail ? { email: target.guestEmail } : {}),
    })
      .then((res) => { if (!cancelled) setResult(res.data); })
      .catch(() => { /* not offered when the check cannot run; the right is still exercisable by email */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return result;
}

/**
 * The visible entry point plus its dialog. Renders nothing unless the server
 * says the function is open for this line.
 */
export function WithdrawFromContract({
  target, label, defaultName, email, onWithdrawn, className,
}: {
  target: WithdrawTarget;
  /** Names the line beside the button when an order has several. */
  label?: string;
  defaultName?: string;
  /** Where the acknowledgement goes: the account's or the order's address. */
  email?: string | null;
  onWithdrawn?: (result: ConfirmResult) => void;
  className?: string;
}) {
  const check = useWithdrawalOpen(target);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(defaultName ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ConfirmResult | null>(null);

  useEffect(() => { if (!open) { setError(null); setSubmitting(false); } }, [open]);
  useEffect(() => { if (defaultName && !name) setName(defaultName); }, [defaultName]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!check?.open && !done) return null;

  const closes = check?.closesAtMs ? new Date(check.closesAtMs).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
  }) : '';
  const statement = `I, ${name.trim() || '[your name]'}, withdraw from my contract for “${target.productName}” (order ${orderRef(target.orderId)}).`;

  const confirm = async () => {
    if (!name.trim()) { setError('Enter your name to confirm the withdrawal.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const call = httpsCallable<Record<string, unknown>, ConfirmResult>(getFunctions(), 'withdrawFromContract');
      const res = await call({
        mode: 'confirm', orderId: target.orderId, productId: target.productId, name: name.trim(),
        ...(target.guestEmail ? { email: target.guestEmail } : {}),
      });
      setDone(res.data);
      onWithdrawn?.(res.data);
    } catch (e: unknown) {
      setError(userErrorMessage(e, 'The withdrawal could not be sent. Try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={className}>
      {done ? (
        <p className="text-sm text-muted-foreground">
          {label ? `${label}: w` : 'W'}ithdrawal received.{' '}
          {done.refundIssued ? 'Your refund is on its way.' : 'Your refund is being processed.'}
        </p>
      ) : (
        <div className="space-y-1">
          <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
            Withdraw from contract here
          </Button>
          <p className="text-xs text-muted-foreground">
            {label ? `${label}: y` : 'Y'}ou can withdraw until {closes}
            {check?.endsOnDownload ?
              (check.endsOnAccessLink ?
                ', as long as you have not started a download or opened an access link.' :
                ', as long as you have not started a download.') :
              '.'}
          </p>
        </div>
      )}
      {/* Not closable while a confirm is in flight: closing re-enabled Confirm,
          and a second confirm only met "already being processed". */}
      <Dialog open={open} onOpenChange={(next) => { if (!submitting) setOpen(next); }}>
        <DialogContent>
          {done ? (
            <>
              <DialogHeader>
                <DialogTitle>Withdrawal received</DialogTitle>
                <DialogDescription>
                  We emailed an acknowledgement{email ? ` to ${email}` : ''}.{' '}
                  {done.refundIssued ?
                    'We are refunding what you paid for this item to your original payment method; refunds usually arrive within 5 to 10 business days.' :
                    'Your refund is being processed and will reach your original payment method within 14 days.'}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose asChild><Button>Done</Button></DialogClose>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Withdraw from contract</DialogTitle>
                <DialogDescription>
                  You are withdrawing from your purchase of {target.productName}. You get back what you paid for it,
                  including its tax and any currency conversion fee, and your access to it ends.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="withdraw-name">Your name</Label>
                  <Input
                    id="withdraw-name"
                    value={name}
                    maxLength={120}
                    autoComplete="name"
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Your statement</p>
                  <p className="rounded-md border bg-muted/30 px-3 py-2 text-sm">{statement}</p>
                </div>
                {email && (
                  <p className="text-xs text-muted-foreground">We will send the acknowledgement to {email}.</p>
                )}
                {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline" disabled={submitting}>Cancel</Button>
                </DialogClose>
                <Button onClick={() => void confirm()} disabled={!name.trim()} loading={submitting}>
                  {submitting ? 'Sending…' : 'Confirm withdrawal'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
